<template>
  <div :class="containerClass">
    <page-header title="Configure Home Page Settings" :container-class="null" class="mb-16pt" :info-text="getLoggedInUser.role_type === USER_ROLE_TYPES.SCHOOL
        ? `Craft an engaging and effective homepage for your site.`
        : ''
      " />

    <b-form class="col-md-12 px-0 page-section pt-0" @submit.prevent="onSubmit">
      <div v-if="!activeSection">
      <b-form-group :label="$t('schoolMsgs.school')" label-for="school" label-class="form-label"
        v-if="get(getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN">
        <v-select id="school" class="form-control v-select-custom" label="name" v-model="school"
          :reduce="school => school.id" placeholder="Select School" :options="allSchools" :loading="areSchoolsLoading"
          @input="prefillLogo(school)">
          <template #search="{ attributes, events }">
            <input class="vs__search" :required="!school" v-bind="attributes" v-on="events" />
          </template>

          <template slot="option" slot-scope="option">
            <div class="d-flex align-items-center">
              <fmv-avatar :title="true" title-class="bg-transparent" rounded no-link slot="aside" size="xs">
                <b-img :src="option.logo_url" class="img-fluid" width="20" alt="Logo" v-if="option.logo_url" />
                <i class="fas fa-university fa-lg" v-else></i>
              </fmv-avatar>
              <span>{{ option.name }}</span>
            </div>
          </template>
          <template slot="selected-option" slot-scope="option">
            {{ option.name }}
          </template>
        </v-select>
        <b-form-invalid-feedback :state="!$v.school.required && $v.school.$dirty ? false : null">This field is
          required.</b-form-invalid-feedback>
      </b-form-group>

      <b-form-group v-if="isSuperAdmin" label-class="form-label">
        <b-form-checkbox id="banner_overlay" v-model="homepageConfig.customUi" switch>
          Use Custom UI?
        </b-form-checkbox>
        <b-btn
          v-if="homepageConfig.customUi"
          variant="primary"
          class="mt-2"
          @click.prevent="openCustomUiEditor"
          :disabled="!school"
        >
          Open Custom UI Editor
        </b-btn>
        <small
          v-if="homepageConfig.customUi && isSuperAdmin && !school"
          class="text-muted d-block mt-1"
        >
          Select a school to edit its custom UI layout.
        </small>
      </b-form-group>

      <page-separator>
        <b-link v-b-toggle="'sections-layouts-section'" class="accordion__toggle p-0">
          <span class="flex">Sections &amp; Layouts</span>
          <md-icon class="accordion__toggle-icon">
            {{ sectionsLayoutsVisible ? 'keyboard_arrow_up' : 'keyboard_arrow_down' }}
          </md-icon>
        </b-link>
      </page-separator>

      <b-collapse id="sections-layouts-section" accordion="sectionsLayoutsAccordionId" :visible="sectionsLayoutsVisible"
        class="accordion__menu" @hide="sectionsLayoutsVisible = false" @show="sectionsLayoutsVisible = true">
        <b-form-group label="Website Theme" label-for="website_theme" label-class="form-label">
          <div class="row">
            <div class="col-md-3 col-6" v-for="opt in themeOptions" :key="`theme-${opt.value}`">
              <div class="layout-thumb text-center clickable-item mb-3" @click.prevent="theme = opt.value">
                <figure class="mb-1">
                  <img width="100%" height="260px" :src="getThemePreviewImg(opt.value)" :alt="opt.text"
                    :class="theme === opt.value ? 'border-outline-primary' : ''" :style="theme === opt.value
                        ? 'object-fit: cover; object-position: top;'
                        : 'border: #f2f2f2 2px solid; border-radius: 8px; object-fit: cover; object-position: top;'
                      " />
                </figure>
                <small :class="theme === opt.value ? 'text-primary font-weight-bold' : 'text-muted'">
                  {{ opt.text }}
                </small>
              </div>
            </div>
          </div>
          <small class="text-muted d-block">
            The theme decides which sections appear on the homepage and how each one looks.
          </small>
        </b-form-group>

        <b-form-group label-class="form-label">
          <b-form-checkbox id="customize_sections" v-model="customizeSections" switch
            @change="onCustomizeSectionsChange">
            Customize sections &amp; layouts?
          </b-form-checkbox>
          <small class="text-muted">
            When off, the homepage uses the selected theme's default section order and layouts.
          </small>
        </b-form-group>

        <b-form-group label="Homepage Sections (drag to reorder)" label-class="form-label">
          <draggable class="draggable-cursor" :list="themeSections" handle=".section-drag-handle">
            <div v-for="(section, index) in themeSections" :key="`theme-section-${index}`"
              class="theme-section-card mb-2 d-flex align-items-center">
              <i class="fas fa-bars icon-16pt mr-3 section-drag-handle clickable-item"></i>
              <strong style="font-size: 14px">{{ sectionLabels[section.type] || section.type }}</strong>
              <span v-if="customizeSections" class="text-muted ml-auto mr-3"
                style="font-size: 12px; white-space: nowrap">
                Layout: <strong>{{ section.variant }}</strong>
              </span>
              <b-btn variant="primary" size="sm" :class="{ 'ml-auto': !customizeSections }"
                :disabled="isSuperAdmin && !school" @click.prevent="openSection(section.type)">
                <i class="fas fa-cog mr-1"></i> Configure
              </b-btn>
            </div>
          </draggable>
          <div class="theme-section-card mb-2 d-flex align-items-center">
            <i class="fas fa-thumbtack icon-16pt mr-3 text-muted"></i>
            <strong style="font-size: 14px">{{ sectionLabels.CTA }}</strong>
            <span v-if="customizeSections" class="text-muted ml-auto mr-3"
              style="font-size: 12px; white-space: nowrap">
              Layout: <strong>{{ ctaSection.variant }}</strong>
            </span>
            <b-btn variant="primary" size="sm" :class="{ 'ml-auto': !customizeSections }"
              :disabled="isSuperAdmin && !school" @click.prevent="openSection('CTA')">
              <i class="fas fa-cog mr-1"></i> Configure
            </b-btn>
          </div>
          <small class="text-muted d-block">
            The bottom section always stays at the end of the homepage.
          </small>
          <small v-if="isSuperAdmin && !school" class="text-muted">
            Select a school to configure its sections.
          </small>
        </b-form-group>
      </b-collapse>
      </div>

      <!-- Section detail view -->
      <template v-if="activeSection">
        <div class="d-flex align-items-center mb-3">
          <b-btn variant="light" size="sm" @click.prevent="goBackToSections">
            <i class="fas fa-arrow-left mr-1"></i> All Sections
          </b-btn>
          <h5 class="mb-0 ml-3">{{ sectionLabels[activeSection] || activeSection }}</h5>
        </div>

        <b-form-group v-if="customizeSections && activeThemeSection" label="Layout"
          label-class="form-label">
          <div class="row">
            <div class="col-md-3 col-6" v-for="variant in sectionVariants[activeSection] || ['Default']"
              :key="`${activeSection}-${variant}`">
              <div class="layout-thumb text-center clickable-item mb-3"
                @click.prevent="activeThemeSection.variant = variant">
                <figure class="mb-1">
                  <img width="100%" height="110px" :src="getSectionLayoutImg(activeSection, variant)" :alt="variant"
                    :class="activeThemeSection.variant === variant ? 'border-outline-primary' : ''" :style="activeThemeSection.variant === variant
                        ? 'object-fit: cover; object-position: top;'
                        : 'border: #f2f2f2 2px solid; border-radius: 8px; object-fit: cover; object-position: top;'
                      " />
                </figure>
                <small :class="activeThemeSection.variant === variant ? 'text-primary font-weight-bold' : 'text-muted'">
                  {{ variant }}
                </small>
              </div>
            </div>
          </div>
        </b-form-group>
      </template>

      <div v-if="activeSection === 'Hero'">
        <b-form-group label="Banner Image" label-for="banner_image" label-class="form-label"
          class="row-align-items-center">
          <b-media class="align-items-center" vertical-align="center">
            <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
              <span v-if="isImgUploading">...</span>
              <b-img :src="homepageConfig.banner_img_url" class="img-fluid" width="40" alt="Image"
                v-else-if="homepageConfig.banner_img_url" />
              <i class="fas fa-university fa-lg" v-else></i>
            </fmv-avatar>

            <image-uploader @image="e => setImage(e, 'banner_image')" :isUploading="isImgUploading" :isRequired="false"
              placeholder="Select Banner Image" />
          </b-media>
        </b-form-group>

        <b-form-group label-class="form-label" v-if="homeLayout.hero.name === 'BgImageBanner'">
          <b-form-checkbox id="banner_overlay" v-model="homepageConfig.bannerOverlay" switch>
            Enable banner overlay?
          </b-form-checkbox>
        </b-form-group>

        <b-form-group label="Text Alignment" label-for="text_alignment" label-class="form-label">
          <div class="row" style="margin-left: -10px">
            <div @click.prevent="
              () => {
                bannerSelectedAlignment = option.value;
              }
            " v-for="option in textAlignOptions" :key="option.value" class="clickable-item py-2 mx-2">
              <div class="card" :class="bannerSelectedAlignment === option.value ? 'border-outline-primary-btn' : ''">
                <md-icon v-b-popover.hover.right :title="option.text" style="font-size: 30px" class=""
                  :class="bannerSelectedAlignment === option.value ? 'text-primary' : ''">{{ option.icon }}</md-icon>
              </div>
            </div>
          </div>
        </b-form-group>

        <b-form-group label="Text Color" label-for="hero_text_color" label-class="form-label"
          v-if="selectedOrganization">
          <div class="d-flex">
            <div class="bg-color-circle mr-2 d-flex" :style="{ background: opt }"
              :class="homeLayout.hero.textColor === opt ? 'active' : ''"
              @click.prevent="homeLayout.hero.textColor = opt" v-for="opt in textColorOpts" :key="opt">
              <md-icon v-if="homeLayout.hero.textColor === opt" class="text-secondary m-auto">check</md-icon>
            </div>
          </div>
        </b-form-group>

        <b-form-group label="Button Color" label-for="hero_btn_color" label-class="form-label"
          v-if="selectedOrganization">
          <div class="d-flex align-items-center">
            <div class="bg-color-circle mr-2 d-flex" :style="{ background: opt }"
              :class="homeLayout.hero.btnColor === opt ? 'active' : ''" @click.prevent="homeLayout.hero.btnColor = opt"
              v-for="opt in btnColorOpts" :key="opt">
              <md-icon v-if="homeLayout.hero.btnColor === opt" class="text-secondary m-auto">check</md-icon>
            </div>
            <div v-if="homeLayout.hero.btnColor" @click.prevent="homeLayout.hero.btnColor = ''" class="clickable-item"
              style="font-size: 12px;">
              Reset
            </div>
          </div>
        </b-form-group>

        <b-form-group label="Background Color" label-for="hero_bg_color" label-class="form-label"
          v-if="selectedOrganization">
          <div class="d-flex">
            <div class="bg-color-circle mr-2 d-flex" :style="{ background: backgroundColorMap[opt.value] }"
              :class="homeLayout.hero.backgroundColor === opt.value ? 'active' : ''"
              @click.prevent="homeLayout.hero.backgroundColor = opt.value" v-for="opt in heroBannerBgColorOpts"
              :key="opt.value">
              <md-icon v-if="homeLayout.hero.backgroundColor === opt.value"
                class="text-secondary m-auto">check</md-icon>
            </div>
          </div>
        </b-form-group>

        <b-form-group label="Title" label-for="title" label-class="form-label">
          <b-form-input id="title" placeholder="Enter Title" v-model="homepageConfig.title" required />
        </b-form-group>

        <b-form-group label="Description" label-for="desc" label-class="form-label">
          <b-form-textarea id="desc" rows="6" v-model="homepageConfig.description" required></b-form-textarea>
        </b-form-group>

        <b-form-group label-class="form-label">
          <b-form-checkbox id="bannerCtaBtn" v-model="homepageConfig.ctaBtn" switch> show CTA button? </b-form-checkbox>
        </b-form-group>
        <div v-if="homepageConfig.ctaBtn">
          <b-form-group label="CTA Title" label-for="title" label-class="form-label">
            <b-form-input id="cta_title" placeholder="Enter CTA Title" v-model="homepageConfig.ctaTitle" required />
          </b-form-group>

          <b-form-group label-class="form-label">
            <b-form-checkbox id="bannerCtaLinkBtn" v-model="homepageConfig.ctaLinkStatus" switch>
              Use default enrollment link
              <span style="color: #c5c5c5d6">
                ({{ get(getLoggedInUser, 'linked_entity.subdomain.subdomain_url') }})
              </span>
            </b-form-checkbox>
          </b-form-group>

          <b-form-group v-if="!homepageConfig.ctaLinkStatus" label="Enrollment Link" label-for="enrollment_link"
            label-class="form-label">
            <b-form-input id="cta_link" placeholder="Enter enrollment link" v-model="homepageConfig.ctaLink" required />
          </b-form-group>
          <b-form-group label="CTA Text Color" label-for="banner_cta_color" label-class="form-label">
            <div class="d-flex">
              <div class="bg-color-circle mr-2 d-flex" :style="{ background: opt === 'default' ? 'transparent' : opt }"
                :class="[
                  homepageConfig.banner_cta_color === opt ? 'active' : '',
                  opt === 'default' ? 'default-color-circle' : '',
                ]" @click.prevent="homepageConfig.banner_cta_color = opt" v-for="opt in ctaTextColorOpts" :key="opt">
                <md-icon v-if="homepageConfig.banner_cta_color === opt" class="text-secondary m-auto">check</md-icon>
                <span v-if="opt === 'default'" class="default-text">Default</span>
              </div>
            </div>
          </b-form-group>
        </div>
      </div>

      <div v-if="activeSection === 'Stats'">
        <b-form-group label-class="form-label">
          <b-form-checkbox id="stats" v-model="homepageConfig.visibleSections.stats" switch>
            Enable this section
          </b-form-checkbox>
        </b-form-group>

        <div v-if="homepageConfig.visibleSections.stats">
          <b-form-group label="Stats Title" label-for="title" label-class="form-label">
            <b-form-input id="stats_title" placeholder="Stats" v-model.trim="homepageConfig.stats_title" />
          </b-form-group>

          <b-form-group label="Stats Description" label-for="stats_desc" label-class="form-label">
            <b-form-textarea id="stats_desc" rows="6" v-model="homepageConfig.stats_desc" required></b-form-textarea>
          </b-form-group>

          <b-row v-for="(st, index) in $v.stats.$each.$iter" :key="index" class="mb-2">
            <b-form-group label="Title" label-for="title" label-class="form-label" class="col-md-6">
              <b-form-input id="title" placeholder="Enter Title" v-model.trim="st.title.$model"
                :state="!st.title.required && st.$dirty ? false : null" />

              <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
            </b-form-group>
            <b-form-group label="Count" label-for="count" label-class="form-label" class="col-md-4">
              <b-form-input id="count" type="number" placeholder="Enter count" v-model.trim="st.count.$model"
                :state="!st.count.required && st.$dirty ? false : null" />

              <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
            </b-form-group>

            <div class="mt-4 pl-2">
              <a class="btn btn-primary btn-md" href="#" @click.prevent="addStat(index)">
                <md-icon>add</md-icon>
              </a>
              <a href="#" class="btn btn-danger ml-2 btn-md" @click.prevent="removeStat(index)" v-if="stats.length > 1">
                <md-icon>close</md-icon>
              </a>
            </div>
          </b-row>

          <section-style-panel v-if="homeLayout.stats" v-model="homeLayout.stats"
            :capabilities="sectionStyleCapabilities.stats" :organization="selectedOrganization" />
        </div>
      </div>

      <!-- Features -->

      <div v-if="activeSection === 'Features'">
        <b-form-group label-class="form-label">
          <b-form-checkbox id="features" v-model="homepageConfig.visibleSections.features" switch>
            Enable this section
          </b-form-checkbox>
        </b-form-group>

        <div v-if="homepageConfig.visibleSections.features">
          <b-form-group label="Feature Title" label-for="feature_title" label-class="form-label">
            <b-form-input id="feature_title" placeholder="Features" v-model.trim="homepageConfig.feature_title" />
          </b-form-group>

          <div v-for="(fe, index) in $v.features.$each.$iter" :key="index" class="mb-2">
            <div class="d-flex justify-content-end">
              <a class="btn btn-primary btn-md" href="#" @click.prevent="addFeat(index)"> Add Feature </a>
              <a href="#" class="btn btn-danger ml-2 btn-md" @click.prevent="removeFeat(index)"
                v-if="features.length > 1">
                Remove Feature
              </a>
            </div>

            <div>
              <page-separator :title="`Feature #${+index + 1}`" />

              <b-form-group label="Image" label-for="image_url" label-class="form-label"
                class="row-align-items-center col-md-12">
                <b-media class="align-items-center" vertical-align="center">
                  <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
                    <span v-if="featuresFile[index].isImgUploading">...</span>
                    <b-img :src="fe.image_url.$model" class="img-fluid" width="40" alt="Image"
                      v-else-if="fe.image_url.$model" />
                    <i class="fas fa-university fa-lg" v-else></i>
                  </fmv-avatar>

                  <image-uploader @image="e => setFeaturesImage(e, index)"
                    :isUploading="featuresFile[index].isImgUploading" :isRequired="false"
                    placeholder="Select Feature Image" />
                </b-media>
              </b-form-group>

              <b-form-group label="Title" label-for="title" label-class="form-label" class="col-md-12">
                <b-form-input id="title" placeholder="Feature" v-model.trim="fe.title.$model"
                  :state="!fe.title.required && fe.$dirty ? false : null" />

                <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
              </b-form-group>

              <b-form-group label="Description" label-for="description" label-class="form-label" class="col-md-12">
                <b-form-textarea id="description" :rows="5" placeholder="Enter description "
                  v-model.trim="fe.$model.description" />
              </b-form-group>
            </div>
          </div>

          <section-style-panel v-if="homeLayout.features" v-model="homeLayout.features"
            :capabilities="sectionStyleCapabilities.features" :organization="selectedOrganization" />
        </div>
      </div>

      <!-- Partners -->

      <div v-if="activeSection === 'Partners'">
        <b-form-group label-class="form-label">
          <b-form-checkbox id="partners" v-model="homepageConfig.visibleSections.partners" switch>
            Enable this section
          </b-form-checkbox>
        </b-form-group>

        <div v-if="homepageConfig.visibleSections.partners">
          <b-form-group label="Partners Title" label-for="partners_title" label-class="form-label">
            <b-form-input id="partners_title" placeholder="Our Partners"
              v-model.trim="homepageConfig.partners_title" />
          </b-form-group>

          <b-form-group label="Partners Description" label-for="partners_desc" label-class="form-label">
            <b-form-textarea id="partners_desc" rows="4" v-model="homepageConfig.partners_desc"></b-form-textarea>
          </b-form-group>

          <div v-for="(pa, index) in $v.partners.$each.$iter" :key="index" class="mb-2">
            <div class="d-flex justify-content-end">
              <a class="btn btn-primary btn-md" href="#" @click.prevent="addPartner(index)"> Add Partner </a>
              <a href="#" class="btn btn-danger ml-2 btn-md" @click.prevent="removePartner(index)"
                v-if="partners.length > 1">
                Remove Partner
              </a>
            </div>

            <div>
              <page-separator :title="`Partner #${+index + 1}`" />

              <b-form-group label="Logo" label-for="logo_url" label-class="form-label"
                class="row-align-items-center col-md-12">
                <b-media class="align-items-center" vertical-align="center">
                  <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
                    <span v-if="partnersFile[index].isImgUploading">...</span>
                    <b-img :src="pa.logo_url.$model" class="img-fluid" width="40" alt="Logo"
                      v-else-if="pa.logo_url.$model" />
                    <i class="fas fa-handshake fa-lg" v-else></i>
                  </fmv-avatar>

                  <image-uploader @image="e => setPartnersImage(e, index)"
                    :isUploading="partnersFile[index].isImgUploading" :isRequired="false"
                    placeholder="Select Partner Logo" />
                </b-media>
              </b-form-group>

              <b-form-group label="Name" label-for="name" label-class="form-label" class="col-md-12">
                <b-form-input id="name" placeholder="Partner name" v-model.trim="pa.name.$model"
                  :state="!pa.name.required && pa.$dirty ? false : null" />

                <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
              </b-form-group>

              <b-form-group label="Link" label-for="url" label-class="form-label" class="col-md-12">
                <b-form-input id="url" placeholder="https://example.com" v-model.trim="pa.$model.url" />
              </b-form-group>
            </div>
          </div>

          <section-style-panel v-if="homeLayout.partners" v-model="homeLayout.partners"
            :capabilities="sectionStyleCapabilities.partners" :organization="selectedOrganization" />
        </div>
      </div>

      <!-- Authorizing Body -->

      <div v-if="activeSection === 'AuthorizingBody'">
        <b-form-group label-class="form-label">
          <b-form-checkbox id="authorizing_body" v-model="homepageConfig.visibleSections.authorizing_body" switch>
            Enable this section
          </b-form-checkbox>
        </b-form-group>

        <div v-if="homepageConfig.visibleSections.authorizing_body">
          <b-form-group label="Authorizing Body Title" label-for="authorizing_body_title" label-class="form-label">
            <b-form-input id="authorizing_body_title" placeholder="Authorizing Body"
              v-model.trim="homepageConfig.authorizing_body_title" />
          </b-form-group>

          <b-form-group label="Authorizing Body Description" label-for="authorizing_body_desc" label-class="form-label">
            <b-form-textarea id="authorizing_body_desc" rows="4"
              v-model="homepageConfig.authorizing_body_desc"></b-form-textarea>
          </b-form-group>

          <div v-for="(ab, index) in authorizingBody" :key="index" class="mb-2">
            <div class="d-flex justify-content-end">
              <a class="btn btn-primary btn-md" href="#" @click.prevent="addAuthorizingBody(index)"> Add Authorizing Body </a>
              <a href="#" class="btn btn-danger ml-2 btn-md" @click.prevent="removeAuthorizingBody(index)"
                v-if="authorizingBody.length > 1">
                Remove Authorizing Body
              </a>
            </div>

            <div>
              <page-separator :title="`Authorizing Body #${+index + 1}`" />

              <b-form-group label="Logo" label-for="ab_logo_url" label-class="form-label"
                class="row-align-items-center col-md-12">
                <b-media class="align-items-center" vertical-align="center">
                  <fmv-avatar :title="true" rounded size="lg" no-link slot="aside">
                    <span v-if="authorizingBodyFile[index].isImgUploading">...</span>
                    <b-img :src="ab.logo_url" class="img-fluid" width="40" alt="Logo" v-else-if="ab.logo_url" />
                    <i class="fas fa-award fa-lg" v-else></i>
                  </fmv-avatar>

                  <image-uploader @image="e => setAuthorizingBodyImage(e, index)"
                    :isUploading="authorizingBodyFile[index].isImgUploading" :isRequired="false"
                    placeholder="Select Authorizing Body Logo" />
                </b-media>
              </b-form-group>

              <b-form-group label="Name" label-for="ab_name" label-class="form-label" class="col-md-12">
                <b-form-input id="ab_name" placeholder="Authorizing body name" v-model.trim="ab.name" />
              </b-form-group>

              <b-form-group label="Link" label-for="ab_url" label-class="form-label" class="col-md-12">
                <b-form-input id="ab_url" placeholder="https://example.com" v-model.trim="ab.url" />
              </b-form-group>
            </div>
          </div>

          <section-style-panel v-if="homeLayout.authorizingBody" v-model="homeLayout.authorizingBody"
            :capabilities="sectionStyleCapabilities.authorizingBody" :organization="selectedOrganization" />
        </div>
      </div>

      <div v-if="activeSection === 'Expertise'">
        <b-form-group label-class="form-label">
          <b-form-checkbox id="expertise" v-model="homepageConfig.visibleSections.expertise" switch>
            Enable this section
          </b-form-checkbox>
        </b-form-group>

        <div v-if="homepageConfig.visibleSections.expertise">
          <b-form-group label="Title" label-for="title" label-class="form-label">
            <b-form-input id="expertise_title" placeholder="Enter Title"
              v-model.trim="homepageConfig.expertise_title" />
          </b-form-group>
          <b-form-group label="Description" label-for="expertise_desc" label-class="form-label">
            <b-form-textarea id="expertise_desc" rows="6" v-model="homepageConfig.expertise_desc"
              required></b-form-textarea>
          </b-form-group>

          <b-row v-for="(st, index) in $v.tags.$each.$iter" :key="index" class="mb-2 p-2">
            <span class="col-md-1 col-1 form-label mt-2 d-flex justify-content-end">Tag</span>

            <b-form-group label-class="form-label" class="col-md-9 col-8">
              <b-form-input :id="`tag-${index}`" placeholder="Enter Tag" v-model.trim="st.title.$model"
                :state="!st.title.required && st.$dirty ? false : null" />

              <b-form-invalid-feedback>This field is required.</b-form-invalid-feedback>
            </b-form-group>

            <div class="col-md-2 col-2 d-flex align-items-start">
              <a class="btn btn-primary btn-md" href="#" @click.prevent="addTag(index)">
                <md-icon>add</md-icon>
              </a>
              <a href="#" class="btn btn-danger ml-2 btn-md" @click.prevent="removeTag(index)" v-if="tags.length > 1">
                <md-icon>close</md-icon>
              </a>
            </div>
          </b-row>

          <section-style-panel v-if="homeLayout.expertise" v-model="homeLayout.expertise"
            :capabilities="sectionStyleCapabilities.expertise" :organization="selectedOrganization" />
        </div>
      </div>

      <div v-if="activeSection === 'Programs'">
        <b-form-group label="Text Alignment" label-for="text_alignment" label-class="form-label">
          <div class="row" style="margin-left: -10px">
            <div @click.prevent="
              () => {
                programSelectedAlignment = option.value;
              }
            " v-for="option in textAlignOptions" :key="option.value" class="clickable-item py-2 mx-2">
              <div class="card" :class="programSelectedAlignment === option.value ? 'border-outline-primary-btn' : ''">
                <md-icon v-b-popover.hover.right :title="option.text" style="font-size: 30px" class=""
                  :class="programSelectedAlignment === option.value ? 'text-primary' : ''">{{ option.icon }}</md-icon>
              </div>
            </div>
          </div>
        </b-form-group>

        <b-form-group label="Title" label-for="prog_title" label-class="form-label">
          <b-form-input id="prog_title" placeholder="Enter Program Title" v-model="homepageConfig.prog_title"
            required />
        </b-form-group>

        <b-form-group label="Number of Programs" label-for="programsCount" label-class="form-label">
          <b-form-input id="programsCount" type="number" min="0" placeholder="Enter number of programs"
            v-model="programsCount" />
        </b-form-group>

        <section-style-panel v-if="homeLayout.programs" v-model="homeLayout.programs"
          :capabilities="sectionStyleCapabilities.programs" :organization="selectedOrganization" />
      </div>

      <div v-if="activeSection === 'Classes'">
        <b-form-group label="Title" label-for="class_title" label-class="form-label">
          <b-form-input id="class_title" placeholder="Enter Class Title" v-model="homepageConfig.class_title"
            required />
        </b-form-group>

        <b-form-group label="Description" label-for="class_desc" label-class="form-label">
          <b-form-textarea id="class_desc" rows="6" v-model="homepageConfig.class_desc"></b-form-textarea>
        </b-form-group>

        <b-form-group label="Video" label-for="classes_video" label-class="form-label" class="row-align-items-center"
          v-if="!homepageConfig.classesVideoUrl">
          <b-media class="align-items-center" vertical-align="center">
            <b-form-file id="classes_video" placeholder="Select Video" :browse-text="$t('generalMsgs.browse')"
              v-model="uploadFile.classes_video" @input="uploadImage('classes_video')" :disabled="isVideoUploading"
              accept="video/mp4, video/webm" />
          </b-media>
        </b-form-group>

        <b-form-group v-if="homepageConfig.classesVideoUrl" label="Classes Video" label-for="classes_video"
          label-class="form-label">
          <div class="d-flex justify-content-end mb-3">
            <b-btn variant="danger" @click="homepageConfig.classesVideoUrl = ''"> Remove </b-btn>
          </div>
          <video width="100%" height="200" controls>
            <source :src="homepageConfig.classesVideoUrl" :type="`video/${videoType}`" />
          </video>
        </b-form-group>

        <section-style-panel v-if="homeLayout.classes" v-model="homeLayout.classes"
          :capabilities="sectionStyleCapabilities.classes" :organization="selectedOrganization" />
      </div>

      <div v-if="activeSection === 'Testimonials'">
        <b-form-group label-class="form-label">
          <b-form-checkbox id="testimonials" v-model="homepageConfig.visibleSections.testimonials" switch>
            Enable this section
          </b-form-checkbox>
        </b-form-group>

        <div v-if="homepageConfig.visibleSections.testimonials">
          <b-form-group label="Title" label-for="testimonial_title" label-class="form-label">
            <b-form-input id="testimonial_title" placeholder="Enter Testimonial Title"
              v-model="homepageConfig.testimonial_title" required />
          </b-form-group>

          <section-style-panel v-if="homeLayout.testimonials" v-model="homeLayout.testimonials"
            :capabilities="sectionStyleCapabilities.testimonials" :organization="selectedOrganization" />
        </div>
      </div>

      <div v-if="activeSection === 'FAQ'">
        <b-form-group label-class="form-label">
          <small class="text-muted d-block mb-2">
            The layout above controls how the FAQ section looks on the homepage. The questions and answers themselves
            are managed on the Configure FAQs page.
          </small>
          <b-btn variant="primary" size="sm"
            :to="{ name: 'faqs', query: { ...(isSuperAdmin && school ? { school_id: school } : {}) } }">
            Manage FAQs
          </b-btn>
        </b-form-group>
      </div>

      <div v-if="activeSection === 'CTA'">
        <b-form-group label-class="form-label">
          <b-form-checkbox id="bottom_section" v-model="homepageConfig.visibleSections.bottom_section" switch>
            Enable this section
          </b-form-checkbox>
        </b-form-group>

        <div v-if="homepageConfig.visibleSections.bottom_section">
          <b-form-group label="Title" label-for="bottom_title" label-class="form-label">
            <b-form-input id="bottom_title" placeholder="Enter Title" v-model="homepageConfig.bottom_title" required />
          </b-form-group>

          <b-form-group label="Description" label-for="bottom_desc" label-class="form-label">
            <b-form-textarea id="bottom_desc" rows="6" v-model="homepageConfig.bottom_desc" required></b-form-textarea>
          </b-form-group>

          <b-form-group label-class="form-label">
            <b-form-checkbox id="bottomCtaBtn" v-model="homepageConfig.ctaBottomBtn" switch>
              show CTA button?
            </b-form-checkbox>
          </b-form-group>

          <div v-if="homepageConfig.ctaBottomBtn">
            <b-form-group label="CTA Title" label-for="title" label-class="form-label">
              <b-form-input id="cta_title" placeholder="Enter CTA Title" v-model="homepageConfig.ctaBottomTitle"
                required />
            </b-form-group>

            <b-form-group label-class="form-label">
              <b-form-checkbox id="bottomCtaLinkBtn" v-model="homepageConfig.ctaBottomLinkStatus" switch>
                Use default enrollment link
                <span style="color: #c5c5c5d6">
                  ({{ get(getLoggedInUser, 'linked_entity.subdomain.subdomain_url') }})
                </span>
              </b-form-checkbox>
            </b-form-group>

            <b-form-group v-if="!homepageConfig.ctaBottomLinkStatus" label="Enrollment Link" label-for="enrollment_link"
              label-class="form-label">
              <b-form-input id="bottom_cta_link" placeholder="Enter enrollment link"
                v-model="homepageConfig.ctaBottomLink" required />
            </b-form-group>
          </div>

          <section-style-panel v-if="homeLayout.cta" v-model="homeLayout.cta"
            :capabilities="sectionStyleCapabilities.cta" :organization="selectedOrganization" />
        </div>
      </div>

      <b-btn variant="primary" :disabled="isFormLoading" style="width: 150px" type="submit" class="btn-normal">
        <i v-if="isLoading" class="fas fa-circle-notch fa-spin"></i>
        <span v-else>Update</span>
      </b-btn>
    </b-form>
  </div>
</template>

<style lang="css" scoped>
.column {
  margin: 10px 10px 0;
  padding: 0;
}

.layout-hover figure img {
  -webkit-transform: scale(1);
  transform: scale(1);
  -webkit-transition: 0.3s ease-in-out;
  transition: 0.3s ease-in-out;
}

.layout-hover figure:hover img {
  -webkit-transform: scale(1.3);
  transform: scale(1.3);
}

.theme-section-card {
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 10px 12px;
  background: #fff;
}

.layout-thumb figure img {
  transition: transform 0.2s ease-in-out;
  border-radius: 8px;
}

.layout-thumb:hover figure img {
  transform: scale(1.08);
}

.layout-disabled {
  position: relative;
  pointer-events: none;
  opacity: 0.5;
}

.layout-disabled::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(200, 200, 200, 0.3);
  border-radius: 10px;
  z-index: 1;
}

.bg-color-circle {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid #868686;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  &.active {
    border: 2px solid var(--secondary-color) !important;
  }
}

.default-color-circle {
  border: 2px dashed #ccc !important;
  position: relative;
}

.default-text {
  font-size: 10px;
  position: absolute;
  bottom: -20px;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
}
</style>

<script>
import { get, isEmpty } from 'lodash';
import axios from 'axios';
import { mapGetters, mapActions } from 'vuex';
import PageHeader from '@/components/Ui/PageHeader.vue';
import { FmvAvatar } from 'fmv-avatar';
import Page from '@/components/Page.vue';
import { USER_ROLES, USER_ROLE_TYPES, SCHOOL_PAYMENT_PLANS, HOMEPAGE_ATTRIB_TYPE } from '@/common/constants';
import { requiredIf, required } from 'vuelidate/lib/validators';
import vSelect from 'vue-select';
import draggable from 'vuedraggable';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import ImageUploader from '@/components/ImageUploader.vue';
import SectionStylePanel, { SECTION_STYLE_DEFAULTS } from './SectionStylePanel.vue';
import {
  THEME_OPTIONS,
  SECTION_VARIANTS,
  DEFAULT_THEME_SECTIONS,
  sectionLayoutImage,
  themePreviewImage,
} from '@/common/sectionLayouts';

// Sections shown in the draggable homepage editor: the theme defaults plus FAQ,
// minus the Bottom Section (CTA) which is pinned at the end separately.
const EDITOR_THEME_SECTIONS = [
  ...DEFAULT_THEME_SECTIONS.filter(section => section.type !== 'CTA'),
  { type: 'FAQ', variant: 'Default' },
];

export default {
  components: {
    PageHeader,
    FmvAvatar,
    vSelect,
    draggable,
    PageSeparator,
    ImageUploader,
    SectionStylePanel,
  },
  extends: Page,
  data() {
    return {
      sectionsLayoutsVisible: true,
      theme: null,
      customizeSections: false,
      themeSections: EDITOR_THEME_SECTIONS.map(section => ({ ...section })),
      ctaSection: { type: 'CTA', variant: 'Default' },
      themeOptions: THEME_OPTIONS,
      sectionVariants: SECTION_VARIANTS,
      sectionLabels: {
        Hero: 'Banner (Hero)',
        Partners: 'Partners',
        AuthorizingBody: 'Authorizing Body',
        Programs: 'Offered Programs',
        Stats: 'Stats',
        Features: 'Features',
        Classes: 'Upcoming Classes',
        Expertise: 'Expertise',
        Testimonials: 'Testimonials',
        FAQ: 'FAQs',
        CTA: 'Bottom Section',
      },
      SCHOOL_PAYMENT_PLANS,
      title: 'Configure Home Page',
      isAdded: false,
      videoType: 'mp4',
      school: null,
      dragging: false,
      homepageConfig: {
        customUi: false,
        customUiData: {},
        title: '',
        description: '',
        banner_img_url: '',
        classesVideoUrl: '',
        ctaBtn: true,
        ctaTitle: '',
        banner_cta_color: 'default',
        ctaLinkStatus: null,
        ctaLink: null,
        attrib_type: [],
        bannerOverlay: true,
        prog_title: '',
        prog_desc: '',
        class_title: '',
        class_desc: '',
        testimonial_title: '',
        testimonial_desc: '',
        bottom_title: '',
        bottom_desc: '',
        bottom_img_url: '',
        expertise_title: '',
        expertise_desc: '',
        stats_title: '',
        stats_desc: '',
        feature_title: '',
        feature_desc: '',
        partners_title: '',
        partners_desc: '',
        authorizing_body_title: '',
        authorizing_body_desc: '',
        bottom_short_title: '',
        ctaBottomBtn: true,
        ctaBottomTitle: '',
        ctaBottomLinkStatus: null,
        ctaBottomLink: null,
        visibleSections: {
          stats: false,
          features: false,
          partners: false,
          authorizing_body: true,
          expertise: true,
          testimonials: true,
          bottom_section: true,
        },
      },
      options: [
        { item: 'stats', name: 'Stats' },
        { item: 'feature', name: 'Feature' },
      ],
      linkedWebsite: null,
      areSchoolsLoading: false,
      allSchools: [],
      isLoading: false,
      isImgUploading: false,
      isBottomImgUploading: false,
      isVideoUploading: false,
      uploadFile: { banner_image: null, bottom_img: null, classes_video: null },
      uploadPercent: { banner_image: 0, bottom_img: 0, classes_video: 0 },
      uploadCancelTokenSource: { banner_image: null, bottom_img: 0, classes_video: 0 },
      USER_ROLES,
      USER_ROLE_TYPES,
      tags: [{ title: '' }],
      stats: [
        {
          count: 0,
          title: '',
        },
      ],
      features: [
        {
          title: '',
          description: '',
          image_url: '',
        },
      ],
      featuresFile: [{ image_url: null, isImgUploading: false }],
      partners: [
        {
          name: '',
          logo_url: '',
          url: '',
        },
      ],
      partnersFile: [{ logo_url: null, isImgUploading: false }],
      authorizingBody: [
        {
          name: '',
          logo_url: '',
          url: '',
        },
      ],
      authorizingBodyFile: [{ logo_url: null, isImgUploading: false }],
      HOMEPAGE_ATTRIB_TYPE,
      textAlignOptions: [
        { text: 'left', icon: 'format_align_left', value: 'left' },
        { text: 'center', icon: 'format_align_center', value: 'center' },
        { text: 'right', icon: 'format_align_right', value: 'right' },
      ],
      bannerSelectedAlignment: 'left',
      progLayout: 'Layout1',
      testimonialsLayout: 'Layout1',
      programSelectedAlignment: 'left',
      programsCount: 6,
      homeLayout: {
        hero: {
          name: 'DefaultBanner',
          textAlignment: 'start',
          backgroundColor: 'light',
          btnColor: '',
          textColor: 'black',
        },
      },
      sectionStyleCapabilities: {
        programs: { showAlignment: true, showText: true, showBackground: true, showButton: true, showCard: true },
        features: { showAlignment: true, showText: true, showBackground: true, showButton: true, showCard: true },
        testimonials: { showAlignment: true, showText: true, showBackground: true, showButton: true, showCard: true },
        classes: { showAlignment: true, showText: true, showBackground: true, showButton: true, showCard: true },
        stats: { showAlignment: true, showText: true, showBackground: true, showButton: false, showCard: false },
        expertise: { showAlignment: true, showText: true, showBackground: true, showButton: false, showCard: false },
        partners: { showAlignment: true, showText: true, showBackground: true, showButton: false, showCard: false },
        authorizingBody: { showAlignment: true, showText: true, showBackground: true, showButton: false, showCard: false },
        cta: { showAlignment: true, showText: true, showBackground: true, showButton: true, showCard: false },
      },
      btnColor: '',
      textAlignmentOpts: [
        { text: 'Left', icon: 'format_align_left', value: 'start' },
        { text: 'Center', icon: 'format_align_center', value: 'center' },
        { text: 'Right', icon: 'format_align_right', value: 'end' },
      ],
      heroTextAlignBanners: ['TextOnlyBanner', 'BgImageBanner'],
      heroBannerBgColorOpts: [
        { text: 'Light', value: 'light' },
        { text: 'Primary', value: 'primary' },
        { text: 'Dark', value: 'dark' },
        { text: 'Secondary', value: 'secondary' },
        { text: 'White', value: 'white' },
        { text: 'Gradient', value: 'gradient' },
      ],
      textColorOpts: ['white', 'black'],
      ctaTextColorOpts: ['white', 'black', 'default'],
    };
  },
  validations() {
    return {
      school: {
        required: requiredIf(() => get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN),
      },
      stats: {
        required: requiredIf(() => this.sectionRequires('Stats', 'stats')),
        $each: {
          count: { required: requiredIf(() => this.sectionRequires('Stats', 'stats')) },
          title: { required: requiredIf(() => this.sectionRequires('Stats', 'stats')) },
        },
      },
      tags: {
        required,
        $each: { title: { required: requiredIf(() => this.sectionRequires('Expertise', 'expertise')) } },
      },
      features: {
        required: requiredIf(() => this.sectionRequires('Features', 'features')),
        $each: {
          title: { required: requiredIf(() => this.sectionRequires('Features', 'features')) },
          image_url: { required: requiredIf(() => this.sectionRequires('Features', 'features')) },
        },
      },
      partners: {
        required: requiredIf(() => this.sectionRequires('Partners', 'partners')),
        $each: {
          name: { required: requiredIf(() => this.sectionRequires('Partners', 'partners')) },
          logo_url: { required: requiredIf(() => this.sectionRequires('Partners', 'partners')) },
        },
      },
    };
  },

  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    ...mapGetters('payoutAcc', ['getPayoutAccountStatus']),
    isFormLoading() {
      return (
        this.isLoading ||
        this.isVideoUploading ||
        this.isImgUploading ||
        this.areSchoolsLoading ||
        this.isFooterImgLoading
      );
    },
    selectedOrganization() {
      if (get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN) {
        return this.allSchools.find(sch => sch.id === this.school);
      } else {
        return this.getLoggedInUser.linked_entity;
      }
    },
    backgroundColorMap() {
      if (this.selectedOrganization) {
        return {
          light: `${this.selectedOrganization.primary_color}15`,
          primary: this.selectedOrganization.primary_color,
          dark: this.selectedOrganization.dark_color,
          secondary: this.selectedOrganization.secondary_color,
          white: '#fff',
          gradient: `linear-gradient(to bottom, ${this.selectedOrganization.primary_color}15, ${this.selectedOrganization.primary_color})`,
        };
      }
      return {};
    },
    btnColorOpts() {
      if (this.selectedOrganization) {
        return [
          `${this.selectedOrganization.primary_color}15`,
          this.selectedOrganization.primary_color,
          this.selectedOrganization.dark_color,
          this.selectedOrganization.secondary_color,
          '#fff',
        ];
      }
      return [];
    },
    isSuperAdmin() {
      return get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN;
    },
    activeSection() {
      const section = this.$route.query.section;
      if (!section || !SECTION_VARIANTS[section]) return null;
      if (this.isSuperAdmin && !this.school) return null;
      return section;
    },
    activeThemeSection() {
      if (this.activeSection === 'CTA') return this.ctaSection;
      return this.themeSections.find(section => section.type === this.activeSection) || null;
    },
    sectionStyleKeys() {
      return Object.keys(this.sectionStyleCapabilities);
    },
  },

  methods: {
    ...mapActions('s3Upload', ['uploadToPresignedUrl']),
    ...mapActions('school', ['getAllSchools', 'getSchool']),
    ...mapActions('webConfig', ['getHomePageConfig', 'createHomepageConfig', 'getLinkedImageUploadPresignedUrl']),
    get,
    isEmpty,

    // Each section is edited on its own Configure screen, so only the section on
    // screen validates its entries — an unfinished section can never block a save
    // made from a different one.
    sectionRequires(sectionType, visibilityKey) {
      return this.activeSection === sectionType && !!this.homepageConfig.visibleSections[visibilityKey];
    },

    ensureSectionStyles() {
      // Make sure homeLayout exists and every eligible section has a style object
      // so the Section Style panels are reactive and round-trip cleanly on save.
      if (!this.homeLayout || typeof this.homeLayout !== 'object') {
        this.homeLayout = { hero: { name: 'DefaultBanner', textAlignment: 'start', backgroundColor: 'light' } };
      }
      this.sectionStyleKeys.forEach(key => {
        this.$set(this.homeLayout, key, { ...SECTION_STYLE_DEFAULTS, ...(this.homeLayout[key] || {}) });
      });
    },

    setImage(file, image) {
      this.uploadFile[image] = file;
      this.uploadImage(image);
    },

    setFeaturesImage(file, index) {
      this.featuresFile[index].image_url = file;
      this.uploadImage('image_url', index);
    },

    setPartnersImage(file, index) {
      this.partnersFile[index].logo_url = file;
      this.uploadImage('partner_image', index);
    },

    setAuthorizingBodyImage(file, index) {
      this.authorizingBodyFile[index].logo_url = file;
      this.uploadImage('authorizing_body_image', index);
    },

    getSectionLayoutImg: sectionLayoutImage,
    getThemePreviewImg: themePreviewImage,
    setAttribute(value) {
      if (!value.length) {
        this.featuresFile = [{ image_url: null, isImgUploading: false }];
        this.features = [
          {
            title: '',
            description: '',
            image_url: '',
          },
        ];
        this.stats = [
          {
            count: 0,
            title: '',
          },
        ];
      } else if (!this.homepageConfig.visibleSections.stats) {
        this.stats = [
          {
            count: 0,
            title: '',
          },
        ];
      } else if (!this.homepageConfig.visibleSections.features) {
        this.featuresFile = [{ image_url: null, isImgUploading: false }];
        this.features = [
          {
            title: '',
            description: '',
            image_url: '',
          },
        ];
      }
    },
    addStat(index) {
      this.stats.splice(index + 1, 0, {
        count: 0,
        title: '',
      });
    },

    removeStat(index) {
      this.stats.splice(index, 1);
    },
    addTag(index) {
      this.tags.splice(index + 1, 0, {
        title: '',
      });
    },

    removeTag(index) {
      this.tags.splice(index, 1);
    },
    addFeat(index) {
      this.features.splice(index + 1, 0, {
        title: '',
        description: '',
        image_url: '',
      });
      this.featuresFile.splice(index + 1, 0, {
        image_url: null,
        isImgUploading: false,
      });
    },

    removeFeat(index) {
      this.features.splice(index, 1);
      this.featuresFile.splice(index, 1);
    },
    addPartner(index) {
      this.partners.splice(index + 1, 0, {
        name: '',
        logo_url: '',
        url: '',
      });
      this.partnersFile.splice(index + 1, 0, {
        logo_url: null,
        isImgUploading: false,
      });
    },

    removePartner(index) {
      this.partners.splice(index, 1);
      this.partnersFile.splice(index, 1);
    },
    addAuthorizingBody(index) {
      this.authorizingBody.splice(index + 1, 0, {
        name: '',
        logo_url: '',
        url: '',
      });
      this.authorizingBodyFile.splice(index + 1, 0, {
        logo_url: null,
        isImgUploading: false,
      });
    },

    removeAuthorizingBody(index) {
      this.authorizingBody.splice(index, 1);
      this.authorizingBodyFile.splice(index, 1);
    },
    openCustomUiEditor() {
      if (this.isSuperAdmin && !this.school) {
        this.makeToast({ variant: 'warning', msg: 'Please select a school first.' });
        return;
      }
      const query = {};
      if (this.isSuperAdmin) {
        query.school = this.school;
      }
      this.$router.push({ name: 'homepage-custom-ui-editor', query });
    },
    resetData() {
      this.linkedWebsite = null;
      this.homepageConfig.title = '';
      this.homepageConfig.description = '';
      this.homepageConfig.banner_img_url = '';
      this.homepageConfig.ctaBtn = true;
      this.homepageConfig.ctaTitle = '';
      this.homepageConfig.banner_cta_color = 'default';
      this.homepageConfig.bannerOverlay = true;
      this.homepageConfig.ctaLinkStatus = null;
      this.homepageConfig.ctaLink = null;
      this.homepageConfig.attrib_type = [];
      this.homepageConfig.prog_title = '';
      this.homepageConfig.prog_desc = '';
      this.homepageConfig.class_title = '';
      this.homepageConfig.class_desc = '';
      this.homepageConfig.classesVideoUrl = '';

      this.homepageConfig.testimonial_title = '';
      this.homepageConfig.testimonial_desc = '';
      this.homepageConfig.bottom_title = '';
      this.homepageConfig.bottom_desc = '';
      this.homepageConfig.bottom_img_url = '';
      this.homepageConfig.expertise_title = '';
      this.homepageConfig.expertise_desc = '';
      this.homepageConfig.stats_title = '';
      this.homepageConfig.stats_desc = '';
      this.homepageConfig.feature_title = '';
      this.homepageConfig.feature_desc = '';
      this.homepageConfig.partners_title = '';
      this.homepageConfig.partners_desc = '';
      this.homepageConfig.authorizing_body_title = '';
      this.homepageConfig.authorizing_body_desc = '';
      this.homepageConfig.bottom_short_title = '';
      this.homepageConfig.ctaBottomBtn = true;
      this.homepageConfig.ctaBottomTitle = '';
      this.homepageConfig.ctaBottomLinkStatus = null;
      this.homepageConfig.ctaBottomLink = null;
      this.homepageConfig.visibleSections = {
        stats: false,
        features: false,
        partners: false,
        authorizing_body: true,
        expertise: true,
        testimonials: true,
        bottom_section: true,
      };
      this.tags = [{ title: '' }];
      this.partnersFile = [{ logo_url: null, isImgUploading: false }];
      this.partners = [
        {
          name: '',
          logo_url: '',
          url: '',
        },
      ];
      this.authorizingBodyFile = [{ logo_url: null, isImgUploading: false }];
      this.authorizingBody = [
        {
          name: '',
          logo_url: '',
          url: '',
        },
      ];
      this.featuresFile = [{ image_url: null, isImgUploading: false }];
      this.features = [
        {
          title: '',
          description: '',
          image_url: '',
        },
      ];
      this.stats = [
        {
          count: 0,
          title: '',
        },
      ];
      this.featuresFile = [{ image_url: null, isImgUploading: false }];
      this.features = [
        {
          title: '',
          description: '',
          image_url: '',
        },
      ];
      this.stats = [
        {
          count: 0,
          title: '',
        },
      ];
      this.theme = null;
      this.themeSections = EDITOR_THEME_SECTIONS.map(section => ({ ...section }));
      this.ctaSection = { type: 'CTA', variant: 'Default' };
      this.customizeSections = false;
    },
    openSection(type) {
      this.$router.push({ query: { ...this.$route.query, section: type } });
      window.scrollTo(0, 0);
    },
    goBackToSections() {
      const query = { ...this.$route.query };
      delete query.section;
      this.$router.push({ query });
      window.scrollTo(0, 0);
    },
    onCustomizeSectionsChange(enabled) {
      if (enabled) {
        this.themeSections = this.normalizeThemeSections(this.themeSections);
      }
    },
    normalizeThemeSections(sections) {
      // Every homepage section is always present exactly once: keep the saved
      // order/variants, then append any missing sections with their defaults.
      // The Bottom Section (CTA) is pinned at the end and tracked separately.
      const seen = {};
      const normalized = [];
      (sections || []).forEach(section => {
        if (section.type === 'CTA') {
          this.ctaSection = { type: 'CTA', variant: section.variant || 'Default' };
          return;
        }
        const known = EDITOR_THEME_SECTIONS.some(def => def.type === section.type);
        if (!known || seen[section.type]) return;
        seen[section.type] = true;
        normalized.push(section);
      });
      EDITOR_THEME_SECTIONS.forEach(def => {
        if (!seen[def.type]) normalized.push({ ...def });
      });
      return normalized;
    },
    prefillLogo(schoolId) {
      this.resetData();
      this.fetchBasicConfig(schoolId);
    },
    async fetchSchools() {
      this.areSchoolsLoading = true;

      const response = await this.getAllSchools({
        sch_payment_plan__title: `${this.SCHOOL_PAYMENT_PLANS.ADVANCED},${this.SCHOOL_PAYMENT_PLANS.ADVANCED_AND_CHAT}`,
      });
      this.allSchools = response.data;

      this.areSchoolsLoading = false;
    },
    async fetchBasicConfig(school_id = null) {
      let resp;
      if (school_id) {
        resp = await this.getHomePageConfig({
          school_id: school_id,
        });
      } else {
        resp = await this.getHomePageConfig({});
      }
      this.linkedWebsite = resp.linked_website;
      this.homepageConfig.title = resp.banner_title;
      this.homepageConfig.description = resp.banner_desc;
      this.homepageConfig.banner_img_url = resp.banner_img;
      this.homepageConfig.ctaBtn = resp.banner_cta_status;
      this.homepageConfig.ctaTitle = resp.banner_cta_title;
      this.homepageConfig.banner_cta_color = resp.banner_cta_color;
      this.homepageConfig.bannerOverlay = resp.banner_overlay;
      this.bannerSelectedAlignment = get(resp, 'banner_text_align') ? get(resp, 'banner_text_align') : 'left';

      this.programSelectedAlignment = get(resp, 'prog_text_align') ? get(resp, 'prog_text_align') : 'left';
      this.progLayout = resp.program_layout;
      this.programsCount = resp.num_of_programs;
      this.testimonialsLayout = resp.testimonial_layout;
      this.homepageConfig.ctaLinkStatus = resp.banner_cta_link === null ? true : false;
      this.homepageConfig.ctaLink = resp.banner_cta_link;
      this.homepageConfig.attrib_type = resp.attrib_type ? resp.attrib_type : [];
      this.homepageConfig.prog_title = resp.prg_title;
      this.homepageConfig.prog_desc = resp.prg_desc;
      this.homepageConfig.class_title = resp.class_title;
      this.homepageConfig.class_desc = resp.class_desc;
      this.homepageConfig.classesVideoUrl = resp.class_video_url;

      this.homepageConfig.testimonial_title = resp.testimonial_title;
      this.homepageConfig.testimonial_desc = resp.testimonial_desc;
      this.homepageConfig.bottom_title = resp.bottom_title;
      this.homepageConfig.bottom_desc = resp.bottom_desc;
      this.homepageConfig.bottom_img_url = resp.bottom_img;
      this.homepageConfig.expertise_title = resp.expertise_title;
      this.homepageConfig.expertise_desc = resp.expertise_desc;
      this.homepageConfig.stats_title = resp.stats_title;
      this.homepageConfig.stats_desc = resp.stats_desc;
      this.homepageConfig.feature_title = resp.feature_title;
      this.homepageConfig.feature_desc = resp.feature_desc;
      this.homepageConfig.partners_title = get(resp, 'attribs.partners_title') || '';
      this.homepageConfig.partners_desc = get(resp, 'attribs.partners_desc') || '';
      this.homepageConfig.authorizing_body_title = get(resp, 'attribs.authorizing_body_title') || '';
      this.homepageConfig.authorizing_body_desc = get(resp, 'attribs.authorizing_body_desc') || '';
      this.homepageConfig.bottom_short_title = resp.bottom_short_title;
      this.homepageConfig.ctaBottomBtn = resp.bottom_cta_status;
      this.homepageConfig.ctaBottomTitle = resp.bottom_cta_title;
      this.homepageConfig.ctaBottomLinkStatus = resp.bottom_cta_link === null ? true : false;
      this.homepageConfig.ctaBottomLink = resp.bottom_cta_link;
      this.homepageConfig.visibleSections = {
        // Default any newly-added section toggle that a pre-existing homepage
        // was saved without, so it renders enabled by default.
        authorizing_body: true,
        ...resp.visible_sections,
      };
      this.homepageConfig.customUi = resp.custom_layout;
      this.homepageConfig.customUiData = resp.custom_layout_data;
      this.theme = resp.theme || null;
      this.customizeSections = !!get(resp, 'theme_sections.length');
      this.ctaSection = { type: 'CTA', variant: 'Default' };
      this.themeSections = this.normalizeThemeSections(this.customizeSections ? resp.theme_sections : []);

      this.options;
      this.stats = get(resp, 'attribs.stats.length')
        ? get(resp, 'attribs.stats')
        : [
          {
            count: 0,
            title: '',
          },
        ];
      this.features = get(resp, 'attribs.features.length')
        ? get(resp, 'attribs.features')
        : [
          {
            title: '',
            description: '',
            image_url: '',
          },
        ];
      if (get(resp, 'attribs.features')) {
        this.featuresFile = this.features.map(() => ({ image_url: null, isImgUploading: false }));
      }
      this.partners = get(resp, 'attribs.partners.length')
        ? get(resp, 'attribs.partners')
        : [
          {
            name: '',
            logo_url: '',
            url: '',
          },
        ];
      if (get(resp, 'attribs.partners')) {
        this.partnersFile = this.partners.map(() => ({ logo_url: null, isImgUploading: false }));
      }
      this.authorizingBody = get(resp, 'attribs.authorizing_body.length')
        ? get(resp, 'attribs.authorizing_body')
        : [
          {
            name: '',
            logo_url: '',
            url: '',
          },
        ];
      if (get(resp, 'attribs.authorizing_body')) {
        this.authorizingBodyFile = this.authorizingBody.map(() => ({ logo_url: null, isImgUploading: false }));
      }
      this.tags = get(resp, 'attrib_tags.length') ? resp.attrib_tags.map(t => ({ title: t })) : [{ title: '' }];

      this.homeLayout = resp.layout;

      if (!this.homeLayout.hero.btnColor) this.homeLayout.hero = Object.assign({ btnColor: '' }, this.homeLayout.hero);
      if (!this.homeLayout.hero.textColor)
        this.homeLayout.hero = Object.assign({ textColor: 'black' }, this.homeLayout.hero);

      this.ensureSectionStyles();
    },
    async uploadImage(uploadType, index) {
      this.uploadCancelTokenSource[uploadType] = axios.CancelToken.source();
      if (uploadType === 'banner_image') {
        this.isImgUploading = true;
      } else if (uploadType === 'bottom_img') {
        this.isBottomImgUploading = true;
      } else if (uploadType === 'classes_video') this.isVideoUploading = true;
      else if (uploadType === 'partner_image') this.partnersFile[index].isImgUploading = true;
      else if (uploadType === 'authorizing_body_image') this.authorizingBodyFile[index].isImgUploading = true;
      else this.featuresFile[index].isImgUploading = true;
      try {
        const urlResp = await this.getLinkedImageUploadPresignedUrl({
          file_name:
            uploadType === 'image_url'
              ? this.featuresFile[index].image_url.name
              : uploadType === 'partner_image'
                ? this.partnersFile[index].logo_url.name
                : uploadType === 'authorizing_body_image'
                  ? this.authorizingBodyFile[index].logo_url.name
                  : this.uploadFile[uploadType].name,
          content_type:
            uploadType === 'image_url'
              ? this.featuresFile[index].image_url.type
              : uploadType === 'partner_image'
                ? this.partnersFile[index].logo_url.type
                : uploadType === 'authorizing_body_image'
                  ? this.authorizingBodyFile[index].logo_url.type
                  : this.uploadFile[uploadType].type,
          upload_type: uploadType,
        });
        await this.uploadToPresignedUrl({
          url: urlResp.upload_url,
          file:
            uploadType === 'image_url'
              ? this.featuresFile[index].image_url
              : uploadType === 'partner_image'
                ? this.partnersFile[index].logo_url
                : uploadType === 'authorizing_body_image'
                  ? this.authorizingBodyFile[index].logo_url
                  : this.uploadFile[uploadType],
          config: {
            onUploadProgress: function (progressEvent) {
              this.uploadPercent[uploadType] = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }.bind(this),
            cancelToken: this.uploadCancelTokenSource[uploadType].token,
          },
        });
        if (uploadType === 'banner_image') {
          this.homepageConfig.banner_img_url = urlResp.upload_url.split('?')[0];
        } else if (uploadType === 'image_url') {
          this.features[index].image_url = urlResp.upload_url.split('?')[0];
        } else if (uploadType === 'partner_image') {
          this.partners[index].logo_url = urlResp.upload_url.split('?')[0];
        } else if (uploadType === 'authorizing_body_image') {
          this.authorizingBody[index].logo_url = urlResp.upload_url.split('?')[0];
        } else if (uploadType === 'bottom_img') {
          this.homepageConfig.bottom_img_url = urlResp.upload_url.split('?')[0];
        } else if (uploadType === 'classes_video') {
          this.homepageConfig.classesVideoUrl = urlResp.upload_url.split('?')[0];
          const makeType = this.homepageConfig.classesVideoUrl.split('.');
          this.videoType = makeType[makeType.length - 1];
        }
      } catch (error) {
        this.uploadFile[uploadType] = null;
      }

      this.uploadCancelTokenSource[uploadType] = null;
      this.uploadPercent[uploadType] = 0;

      if (uploadType === 'banner_image') this.isImgUploading = false;
      else if (uploadType === 'bottom_img') this.isBottomImgUploading = false;
      else if (uploadType === 'classes_video') this.isVideoUploading = false;
      else if (uploadType === 'partner_image') this.partnersFile[index].isImgUploading = false;
      else if (uploadType === 'authorizing_body_image') this.authorizingBodyFile[index].isImgUploading = false;
      else this.featuresFile[index].isImgUploading = false;
    },

    async onSubmit() {
      this.isLoading = true;
      this.$v.$touch();
      if (!this.$v.$invalid) {
        try {
          const data = {
            linked_website: this.linkedWebsite,
            banner_title: this.homepageConfig.title,
            banner_desc: this.homepageConfig.description,
            banner_img: this.homepageConfig.banner_img_url,
            banner_overlay: this.homepageConfig.bannerOverlay,
            banner_cta_status: this.homepageConfig.ctaBtn,
            banner_cta_title: this.homepageConfig.ctaTitle,
            banner_cta_color: this.homepageConfig.banner_cta_color,
            banner_cta_link: this.homepageConfig.ctaLinkStatus ? null : this.homepageConfig.ctaLink,
            banner_text_align: this.bannerSelectedAlignment,
            attrib_type: this.homepageConfig.attrib_type,
            attribs: {
              stats: this.homepageConfig.visibleSections.stats ? this.stats : [],
              features: this.homepageConfig.visibleSections.features ? this.features : [],
              partners: this.homepageConfig.visibleSections.partners ? this.partners : [],
              partners_title: this.homepageConfig.partners_title,
              partners_desc: this.homepageConfig.partners_desc,
              // Blank placeholder rows would render as an empty section, so only send filled ones.
              authorizing_body: this.homepageConfig.visibleSections.authorizing_body
                ? this.authorizingBody.filter(ab => ab.name || ab.logo_url)
                : [],
              authorizing_body_title: this.homepageConfig.authorizing_body_title,
              authorizing_body_desc: this.homepageConfig.authorizing_body_desc,
            },
            bottom_title: this.homepageConfig.bottom_title,
            bottom_desc: this.homepageConfig.bottom_desc,
            bottom_img: this.homepageConfig.bottom_img_url,

            bottom_cta_status: this.homepageConfig.ctaBottomBtn,
            bottom_cta_title: this.homepageConfig.ctaBottomTitle,
            bottom_cta_link: this.homepageConfig.ctaBottomLinkStatus ? null : this.homepageConfig.ctaBottomLink,

            stats_title: this.homepageConfig.stats_title,
            stats_desc: this.homepageConfig.stats_desc,
            feature_title: this.homepageConfig.feature_title,
            feature_desc: this.homepageConfig.feature_desc,
            prg_title: this.homepageConfig.prog_title,
            prg_desc: this.homepageConfig.prog_desc,

            prog_text_align: this.programSelectedAlignment,
            program_layout: this.progLayout,
            num_of_programs: this.programsCount,
            testimonial_layout: this.testimonialsLayout,
            testimonial_title: this.homepageConfig.testimonial_title,
            testimonial_desc: this.homepageConfig.testimonial_desc,
            class_title: this.homepageConfig.class_title,
            class_desc: this.homepageConfig.class_desc,
            class_video_url: this.homepageConfig.classesVideoUrl,
            bottom_short_title: this.homepageConfig.bottom_short_title,
            expertise_title: this.homepageConfig.expertise_title,
            expertise_desc: this.homepageConfig.expertise_desc,
            attrib_tags: this.tags.filter(tag => !!tag.title).map(tag => tag.title),
            visible_sections: this.homepageConfig.visibleSections,

            layout: this.homeLayout,
            custom_layout: this.homepageConfig.customUi,
            custom_layout_data: this.homepageConfig.customUiData,
            theme: this.theme,
            theme_sections: this.customizeSections ? [...this.themeSections, { ...this.ctaSection }] : [],

            school:
              get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN
                ? this.school
                : this.getLoggedInUser.linked_entity.id,
          };
          await this.createHomepageConfig({
            ...data,
          });
          this.makeToast({ variant: 'success', msg: 'Settings Configured!' });
        } catch (err) {
          this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        }
      } else {
        this.makeToast({ variant: 'danger', msg: 'Please fill all fields correctly.' });
      }
      this.isLoading = false;
    },
  },

  async mounted() {
    this.isLoading = true;

    this.ensureSectionStyles();

    if (get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN) {
      this.fetchSchools();
    }
    if (
      get(this.getLoggedInUser, 'role') === USER_ROLES.SCHOOL_OWNER ||
      get(this.getLoggedInUser, 'role') === USER_ROLES.SCHOOL_ADMIN
    ) {
      (await this.getSchool(this.getLoggedInUser.linked_entity.id)).data;
    }
    try {
      if (
        get(this.getLoggedInUser, 'role') === USER_ROLES.SCHOOL_OWNER ||
        get(this.getLoggedInUser, 'role') === USER_ROLES.SCHOOL_ADMIN
      ) {
        this.fetchBasicConfig();
      }
    } catch (e) {
      this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
    }

    this.isLoading = false;
  },
};
</script>
